import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Plus, Search, Filter, ShieldAlert } from 'lucide-react';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Incidents.module.css';

const Incidents = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/security/incidents');
      setIncidents(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ type: 'Safety', location: '', description: '', severity: 'Medium' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/security/incidents', formData);
      setIncidents([data, ...incidents]);
      setIsDrawerOpen(false);
      setFormData({ type: 'Safety', location: '', description: '', severity: 'Medium' });
      toast.success('Incident logged successfully!');
    } catch (err) {
      toast.error('Failed to log incident');
    }
  };

  const getSeverityClass = (sev) => {
    if(sev === 'High') return styles.sevHigh;
    if(sev === 'Medium') return styles.sevMedium;
    return styles.sevLow;
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Incident Reports</h1>
          <p>Log and track security incidents or rule violations</p>
        </div>
        <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
          <Plus size={16} /> Log Incident
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search incidents..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Type & Location</th>
                <th>Description</th>
                <th>Severity</th>
                <th>Time Logged</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="6">Loading...</td></tr> : incidents.map(inc => (
                <tr key={inc._id}>
                  <td><strong>{inc._id.substring(inc._id.length - 6).toUpperCase()}</strong></td>
                  <td>
                    <div className={styles.typeInfo}>
                      <strong>{inc.type}</strong>
                      <span>{inc.location}</span>
                    </div>
                  </td>
                  <td>
                    <p className={styles.descText}>{inc.description}</p>
                  </td>
                  <td>
                    <span className={`${styles.badgeSev} ${getSeverityClass(inc.severity)}`}>
                      {inc.severity}
                    </span>
                  </td>
                  <td><span className={styles.timeText}>{new Date(inc.createdAt).toLocaleString([], {month:'short', day:'numeric', hour: '2-digit', minute:'2-digit'})}</span></td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[inc.status.toLowerCase()]}`}>
                      {inc.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.mobileList}>
            {loading ? <p style={{padding: '16px', textAlign: 'center'}}>Loading...</p> : incidents.map(inc => (
              <div key={inc._id} className={styles.mobileCard}>
                <div className={styles.cardTop}>
                  <div className={styles.typeInfo}>
                    <strong>{inc.type}</strong>
                    <span>{inc.location}</span>
                  </div>
                  <span className={`${styles.badgeStatus} ${styles[inc.status.toLowerCase()]}`}>
                    {inc.status}
                  </span>
                </div>
                <div className={styles.cardMid}>
                  <p className={styles.descText}>{inc.description}</p>
                </div>
                <div className={styles.cardBottom}>
                  <span className={styles.timeText}>{new Date(inc.createdAt).toLocaleString([], {month:'short', day:'numeric', hour: '2-digit', minute:'2-digit'})}</span>
                  <span className={`${styles.badgeSev} ${getSeverityClass(inc.severity)}`}>
                    {inc.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Log New Incident">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Incident Type</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="Safety">Safety & Security</option>
              <option value="Rule Violation">Rule Violation</option>
              <option value="Maintenance">Maintenance Issue</option>
              <option value="Medical">Medical Emergency</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Location</label>
            <input name="location" value={formData.location} onChange={handleChange} required placeholder="e.g. Main Gate, Tower A Lobby" />
          </div>
          <div className="formGroup">
            <label>Severity Level</label>
            <select name="severity" value={formData.severity} onChange={handleChange}>
              <option value="Low">Low (General observation)</option>
              <option value="Medium">Medium (Needs attention)</option>
              <option value="High">High (Immediate action required)</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} required rows="4" placeholder="Describe the incident in detail..."></textarea>
          </div>
          
          <div className={styles.alertBox}>
            <ShieldAlert size={20} className={styles.alertIcon} />
            <p>High severity incidents will immediately notify the Admin and Committee Members.</p>
          </div>

          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Log Incident</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Incidents;
